export type InstrumentReportSort = 'date_desc' | 'portfolio_exposure_desc';

const REPORT_TYPE_ALIASES: Record<string, string> = {
  'fato relevante': 'Fato Relevante',
  'fato relev': 'Fato Relevante',
};

function reportTypeKey(value: string) {
  return value
    .trim()
    .toLocaleLowerCase('pt-BR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ');
}

export function canonicalizeInstrumentReportType(value: string) {
  const trimmed = value.trim();
  if (!trimmed) return '';
  return REPORT_TYPE_ALIASES[reportTypeKey(trimmed)] ?? trimmed;
}

export function collectReportTypes(
  reports: Array<{
    semanticType?: string | null;
    documentType?: string | null;
    category?: string | null;
  }>,
) {
  const types = new Map<string, string>();
  reports.forEach((report) => {
    const type = report.semanticType ?? report.documentType ?? report.category ?? '';
    const canonicalType = canonicalizeInstrumentReportType(type);
    if (canonicalType) {
      const key = reportTypeKey(canonicalType);
      if (!types.has(key)) types.set(key, canonicalType);
    }
  });
  return Array.from(types.values()).sort((left, right) => left.localeCompare(right));
}

export function withReportDateRange(
  query: URLSearchParams,
  {
    startDate,
    endDate,
  }: {
    startDate: string;
    endDate: string;
  },
) {
  const nextQuery = new URLSearchParams(query.toString());
  if (!nextQuery.get('reportStartDate') && startDate) {
    nextQuery.set('reportStartDate', startDate);
  }
  if (!nextQuery.get('reportEndDate') && endDate) {
    nextQuery.set('reportEndDate', endDate);
  }
  return nextQuery;
}

type SortableInstrumentReport = {
  deliveryDate: string | null;
  portfolioExposure?: number | null;
};

export function sortInstrumentReports<T extends SortableInstrumentReport>(
  reports: T[],
  sort: InstrumentReportSort,
): T[] {
  return [...reports].sort((left, right) => {
    if (sort === 'portfolio_exposure_desc') {
      const leftExposure = left.portfolioExposure;
      const rightExposure = right.portfolioExposure;
      if (leftExposure == null && rightExposure != null) return 1;
      if (leftExposure != null && rightExposure == null) return -1;
      const exposureDifference = (rightExposure ?? 0) - (leftExposure ?? 0);
      if (exposureDifference !== 0) {
        return exposureDifference;
      }
    }

    const rightTime = right.deliveryDate ? new Date(right.deliveryDate).getTime() : Number.NEGATIVE_INFINITY;
    const leftTime = left.deliveryDate ? new Date(left.deliveryDate).getTime() : Number.NEGATIVE_INFINITY;
    return rightTime - leftTime;
  });
}
